import * as React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { TextField } from '@mui/material';
import Button from '@mui/material/Button';
import styled from '@emotion/styled';
import InputLabel from '@mui/material/InputLabel';
import device from '../../../../style/devices';
import FileSourceAutocomplete from './FileSourceAutocomplete';
import FileTypeAutocomplete from './FileTypeAutocomplete';
import { DisplayFormInputValue } from '../../../../interfaces/interfaces';
import { GeneralSourceAndTypeBundle } from './FileSourceAutocomplete/FileSourceAutocomplete';

const StyledContainerButton = styled.div`
  display: flex;
  @media (${device.mobile}) {
    flex-direction: column;
  }
`;

const StyledWrapperButton = styled.div`
  margin-right: 2rem;

  @media (${device.mobile}) {
    flex: 1;
    margin: 1rem 0;
  }
}
`;

const StyledWrapperInput = styled.div`
  margin-bottom: 1rem;
`;

const StyledContainerForm = styled.div`
  padding: 5rem;
  @media (${device.mobile}) {
    padding: 2rem 0;
  }
`;

const StyledInputDivContainer = styled.div`
  display: flex;
  @media (${device.mobile}) {
    flex-direction: column;
  }
`;

const StyledInputDiv = styled.div`
  width: 50%;
  @media (${device.mobile}) {
    width: 100%;
  }
`;
const StyledTextContainer = styled.p`
  width: 50%;
  margin-top: 0;
  padding: 0 1rem;
  @media (${device.mobile}) {
    width: 100%;
  }
`;

const StyledStatusDiv = styled.div`
  margin: auto 0;
`;

const UploadFileForm = () => {
  const [fileTypeValue, setFileTypeValue] = React.useState<DisplayFormInputValue | null>(null);
  const [fileContentValue, setFileContentValue] = React.useState<DisplayFormInputValue | null>(null);
  const [fileVersion, setFileVersion] = React.useState<string>('');
  const [isValidFileType, setIsValidFileType] = React.useState(true);
  const [fileSourceValue, setFileSourceValue] = React.useState<GeneralSourceAndTypeBundle | null>(null);
  const [status, setStatus] = React.useState<string>('');
  const [optionsFileType, setOptionsFileType] = React.useState<DisplayFormInputValue[] | []>([]);
  const [optionsContent, setOptionsContent] = React.useState<DisplayFormInputValue[] | []>([]);
  const inputRef = React.useRef<HTMLFormElement>(null);

  const { getAccessTokenSilently, user } = useAuth0();

  const onSubmit = React.useCallback(
    async (e: any) => {
      e.preventDefault();

      const body = new FormData(e.target);
      body.append('user_name', `${user?.name ?? '--'} `);
      body.append('user_id', `${user?.sub ?? '--'} `);
      body.append('file_content_id', `${fileContentValue?.id}`);
      body.delete('type');
      body.delete('content');
      body.delete('source_name');

      const url = `${process.env.REACT_APP_URL_HOST}/admin/v1/imports`;
      const accessToken = await getAccessTokenSilently();
      const headers = {
        Authorization: `Bearer ${accessToken}`,
      };
      fetch(url, {
        method: 'post',
        body,
        headers,
      })
        .then((res) => {
          setStatus('ok');
          if (res.status < 200 || res.status >= 300) throw new Error(`Request failed with status ${res.status}`);
          return res.json();
        })
        .then(() => {
          setStatus('File uploaded successfully!!');
        })
        .catch((err) => setStatus(`Upload file failed! ${err.message}`));

      setFileTypeValue(null);
      setFileContentValue(null);
      setFileVersion('');
      setIsValidFileType(true);
      setFileSourceValue(null);
      setOptionsFileType([]);
      setOptionsContent([]);
      inputRef?.current?.reset();
    },
    [getAccessTokenSilently, fileContentValue, user]
  );

  const handleFileSourceInput = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsValidFileType(!!e.target.value.match(/(\.csv)$/i));
    },
    [setIsValidFileType]
  );

  const handleFileVersionInput = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFileVersion(e.target.value);
    },
    [setFileVersion]
  );

  // reset button
  const onResetFields = React.useCallback(() => {
    setFileTypeValue(null);
    setFileContentValue(null);
    setFileVersion('');
    setIsValidFileType(true);
    setFileSourceValue(null);
    setStatus('');
    setOptionsFileType([]);
    setOptionsContent([]);
    inputRef?.current?.reset();
  }, []);

  // useEffect for cleaning file type and file content inputs
  React.useEffect(() => {
    if (!fileTypeValue && !fileContentValue) return;
    setFileTypeValue(null);
    setFileContentValue(null);
  }, [fileSourceValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect for cleaning file content inputs
  React.useEffect(() => {
    if (fileContentValue) setFileContentValue(null);
  }, [fileTypeValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // prevent leak content after unmounting component
  React.useEffect(() => {
    return () => {
      setFileTypeValue(null);
      setFileContentValue(null);
      setFileVersion('');
      setIsValidFileType(true);
      setFileSourceValue(null);
      setStatus('');
      setOptionsFileType([]);
      setOptionsContent([]);
    };
  }, []);

  return (
    <StyledContainerForm>
      <form onSubmit={onSubmit} id="myForm" ref={inputRef}>
        <StyledWrapperInput>
          <InputLabel>File Source*</InputLabel>
          <StyledInputDivContainer>
            <StyledInputDiv>
              <FileSourceAutocomplete value={fileSourceValue} setValue={setFileSourceValue} nameKeyForm="source_name" placeholder="file source" />
            </StyledInputDiv>
            <StyledTextContainer>What’s the source? Scott’s Directories. Google APIs, some other data provider?</StyledTextContainer>
          </StyledInputDivContainer>
        </StyledWrapperInput>
        <StyledWrapperInput>
          <InputLabel>File Type*</InputLabel>
          <StyledInputDivContainer>
            <StyledInputDiv>
              <FileTypeAutocomplete
                value={fileTypeValue}
                setValue={setFileTypeValue}
                options={optionsFileType}
                setOptions={setOptionsFileType}
                searchValue={fileSourceValue}
                name="type"
                placeholder="file type"
                routeValue="/file_types"
              />
            </StyledInputDiv>
            <StyledTextContainer>This is the highest level in the hierarchy, either an Organization or a Practitioner.</StyledTextContainer>
          </StyledInputDivContainer>
        </StyledWrapperInput>
        <StyledWrapperInput>
          <InputLabel>File Content*</InputLabel>
          <StyledInputDivContainer>
            <StyledInputDiv>
              <FileTypeAutocomplete
                value={fileContentValue}
                setValue={setFileContentValue}
                options={optionsContent}
                setOptions={setOptionsContent}
                searchValue={fileTypeValue}
                name="content"
                placeholder="file content"
                routeValue="/file_contents"
              />
            </StyledInputDiv>
            <StyledTextContainer>What’s the content of the file? Physician, pharmacy, lab and diagnostic center and such.</StyledTextContainer>
          </StyledInputDivContainer>
        </StyledWrapperInput>
        <StyledWrapperInput>
          <InputLabel>File Version</InputLabel>
          <StyledInputDivContainer>
            <StyledInputDiv>
              <TextField
                size="small"
                name="file_version"
                placeholder="file version"
                sx={{ width: '100%', bgcolor: '#ffffff', borderRadius: '5px' }}
                value={fileVersion}
                onChange={handleFileVersionInput}
              />
            </StyledInputDiv>
            <StyledTextContainer>Last version used was</StyledTextContainer>
          </StyledInputDivContainer>
        </StyledWrapperInput>
        <StyledWrapperInput>
          <InputLabel>Browse File*</InputLabel>
          <StyledInputDivContainer>
            <StyledInputDiv>
              <TextField
                required
                id="file_uploads"
                size="small"
                type="file"
                sx={{
                  width: '100%',
                  bgcolor: '#ffffff',
                  borderRadius: '5px',
                  '& ::file-selector-button': { display: 'none' },
                }}
                name="file"
                placeholder=""
                error={!isValidFileType}
                helperText={!isValidFileType && 'The following file types are allowed : CSV.'}
                onChange={handleFileSourceInput}
              />
            </StyledInputDiv>
            <StyledTextContainer> </StyledTextContainer>
          </StyledInputDivContainer>
        </StyledWrapperInput>
        <StyledContainerButton>
          <StyledWrapperButton>
            <Button type="submit" variant="contained" disabled={!isValidFileType} fullWidth>
              Upload
            </Button>
          </StyledWrapperButton>
          <StyledWrapperButton>
            <Button type="reset" variant="contained" onClick={onResetFields} fullWidth>
              Reset
            </Button>
          </StyledWrapperButton>
          <StyledStatusDiv>{status}</StyledStatusDiv>
        </StyledContainerButton>
      </form>
    </StyledContainerForm>
  );
};

export default UploadFileForm;
